import { WorkflowBlock, PortSide } from '@/types/workflow';

export interface Point {
  x: number;
  y: number;
}

export interface PortPosition {
  x: number;
  y: number;
  side: PortSide;
}

/**
 * Get the absolute position for a specific port on a block (center-origin coordinates).
 */
export function getPortPosition(
  block: WorkflowBlock,
  w: number,
  h: number,
  port: PortSide
): PortPosition {
  switch (port) {
    case 'top':          return { x: block.x,         y: block.y - h / 2, side: 'top' };
    case 'top-right':    return { x: block.x + w / 2, y: block.y - h / 2, side: 'top-right' };
    case 'right':        return { x: block.x + w / 2, y: block.y,         side: 'right' };
    case 'bottom-right': return { x: block.x + w / 2, y: block.y + h / 2, side: 'bottom-right' };
    case 'bottom':       return { x: block.x,         y: block.y + h / 2, side: 'bottom' };
    case 'bottom-left':  return { x: block.x - w / 2, y: block.y + h / 2, side: 'bottom-left' };
    case 'left':         return { x: block.x - w / 2, y: block.y,         side: 'left' };
    case 'top-left':     return { x: block.x - w / 2, y: block.y - h / 2, side: 'top-left' };
  }
}

/**
 * Return unit outward vector for a given port side.
 */
export function getOutwardVector(side: PortSide): { dx: number; dy: number } {
  switch (side) {
    case 'top':          return { dx: 0,      dy: -1 };
    case 'top-right':    return { dx: 0.707,  dy: -0.707 };
    case 'right':        return { dx: 1,      dy: 0 };
    case 'bottom-right': return { dx: 0.707,  dy: 0.707 };
    case 'bottom':       return { dx: 0,      dy: 1 };
    case 'bottom-left':  return { dx: -0.707, dy: 0.707 };
    case 'left':         return { dx: -1,     dy: 0 };
    case 'top-left':     return { dx: -0.707, dy: -0.707 };
  }
}

const ALL_PORTS: PortSide[] = [
  'top', 'top-right', 'right', 'bottom-right',
  'bottom', 'bottom-left', 'left', 'top-left',
];

/**
 * Mirror a port across the block's horizontal axis (Y-flip): top sides
 * move to bottom and vice versa, left/right stay put.
 *
 *   top          ↔ bottom
 *   top-right    ↔ bottom-right
 *   top-left     ↔ bottom-left
 *   left  / right → unchanged (no Y component)
 *
 * Picked over a 180° rotation because users expect the return line to
 * sit *parallel* to the original (mirrored across the connecting axis),
 * not to wrap around to the far corner.
 */
const Y_FLIP_PORT: Record<PortSide, PortSide> = {
  'top': 'bottom',
  'bottom': 'top',
  'right': 'right',
  'left': 'left',
  'top-right': 'bottom-right',
  'bottom-right': 'top-right',
  'top-left': 'bottom-left',
  'bottom-left': 'top-left',
};

/**
 * Mirror across the vertical axis (X-flip): left ↔ right, top/bottom
 * stay put. Used as a fallback when Y-flip doesn't separate the return
 * line from the original (e.g. pure left/right cardinal pairs).
 */
const X_FLIP_PORT: Record<PortSide, PortSide> = {
  'top': 'top',
  'bottom': 'bottom',
  'right': 'left',
  'left': 'right',
  'top-right': 'top-left',
  'top-left': 'top-right',
  'bottom-right': 'bottom-left',
  'bottom-left': 'bottom-right',
};

/** Y-flip mirror — exposed so other code (e.g. tests, future helpers) can use it. */
export function mirrorPortVertical(side: PortSide): PortSide {
  return Y_FLIP_PORT[side];
}

/** X-flip mirror. */
export function mirrorPortHorizontal(side: PortSide): PortSide {
  return X_FLIP_PORT[side];
}

/**
 * Picks ports for a "return flow" connector — the second line in a
 * dual-directional flow (A→B + B→A) using a parallel path on the
 * other side of each block.
 *
 * Strategy:
 *   1. Mirror both ports vertically (Y-flip). For top→top this gives
 *      bottom→bottom; for top-right→top-left it gives bottom-right→
 *      bottom-left — exactly the parallel-below line a user would draw
 *      manually.
 *   2. If Y-flip leaves both ports unchanged (pure left/right pair on
 *      a horizontal layout) the return would mirror the original and
 *      `createConnector`'s toggle-remove rule would delete it instead.
 *      Fall back to vertical-axis offset by adding a `bottom-` prefix
 *      via a diagonal lookup, so right→left gives bottom-right→
 *      bottom-left.
 *   3. Final safety: if even the fallback would collide, X-flip the
 *      pair so we still get two distinct lines.
 */
export function getReturnPortPair(
  fromPort: PortSide, toPort: PortSide,
): { fromPort: PortSide; toPort: PortSide } {
  const tryPair = (rfp: PortSide, rtp: PortSide) => {
    // The toggle-remove rule fires when the new (rfp, rtp) on the
    // reversed endpoint pair equals the original (fp, tp) or its swap.
    const collidesForward = rfp === fromPort && rtp === toPort;
    const collidesReverse = rfp === toPort && rtp === fromPort;
    return !collidesForward && !collidesReverse;
  };

  // 1) Y-flip both ports.
  const yFrom = Y_FLIP_PORT[toPort];
  const yTo = Y_FLIP_PORT[fromPort];
  if (tryPair(yFrom, yTo)) {
    return { fromPort: yFrom, toPort: yTo };
  }

  // 2) Diagonal-below fallback for cardinal horizontal pairs.
  const DIAG_BELOW: Record<PortSide, PortSide> = {
    'left': 'bottom-left',
    'right': 'bottom-right',
    'top': 'bottom',
    'bottom': 'top',
    'top-left': 'bottom-left',
    'top-right': 'bottom-right',
    'bottom-left': 'top-left',
    'bottom-right': 'top-right',
  };
  const dFrom = DIAG_BELOW[toPort];
  const dTo = DIAG_BELOW[fromPort];
  if (tryPair(dFrom, dTo)) {
    return { fromPort: dFrom, toPort: dTo };
  }

  // 3) X-flip safety net.
  return {
    fromPort: X_FLIP_PORT[toPort],
    toPort: X_FLIP_PORT[fromPort],
  };
}

/**
 * Picks the *first available* return port pair given a set of already
 * occupied (block, port) keys for the relation between two blocks. Used
 * by the "Skapa returflöde"-button in the connector popover so it can
 * skip combinations that would be rejected by the port-uniqueness rule.
 *
 * `usedKeys` contains entries shaped like `${blockId}:${port}`. The
 * function walks Y-flip → diagonal-below → exhaustive search and
 * returns the first pair where neither the source nor the target port
 * is already present. Returns `null` when no port combination remains
 * (e.g. all 8 ports on one of the blocks are already linked to the
 * other block).
 */
export function getAvailableReturnPortPair(
  fromBlockId: string, toBlockId: string,
  fromPort: PortSide, toPort: PortSide,
  usedKeys: ReadonlySet<string>,
): { fromPort: PortSide; toPort: PortSide } | null {
  const collidesWithOriginal = (rfp: PortSide, rtp: PortSide) =>
    (rfp === fromPort && rtp === toPort) ||
    (rfp === toPort && rtp === fromPort);

  // Return-connector flips endpoints, so its from-block is the original
  // to-block. Keep the keys consistent.
  const isFree = (rfp: PortSide, rtp: PortSide) =>
    !usedKeys.has(`${toBlockId}:${rfp}`) &&
    !usedKeys.has(`${fromBlockId}:${rtp}`) &&
    !collidesWithOriginal(rfp, rtp);

  // Tier 1: Y-flip (parallel return).
  const yFrom = Y_FLIP_PORT[toPort];
  const yTo = Y_FLIP_PORT[fromPort];
  if (isFree(yFrom, yTo)) return { fromPort: yFrom, toPort: yTo };

  // Tier 2: diagonal-below.
  const DIAG_BELOW: Record<PortSide, PortSide> = {
    'left': 'bottom-left',
    'right': 'bottom-right',
    'top': 'bottom',
    'bottom': 'top',
    'top-left': 'bottom-left',
    'top-right': 'bottom-right',
    'bottom-left': 'top-left',
    'bottom-right': 'top-right',
  };
  const dFrom = DIAG_BELOW[toPort];
  const dTo = DIAG_BELOW[fromPort];
  if (isFree(dFrom, dTo)) return { fromPort: dFrom, toPort: dTo };

  // Tier 3: exhaustive 8×8 search — preferred order biases toward
  // visually parallel choices (mirrored sides of the original ports).
  for (const rfp of ALL_PORTS) {
    for (const rtp of ALL_PORTS) {
      if (isFree(rfp, rtp)) return { fromPort: rfp, toPort: rtp };
    }
  }
  return null;
}

// Auto-routing helpers (getOptimalPorts, generateSmartWaypoints,
// generateAutoWaypoints, buildCatmullRomCurve) removed alongside the
// connector auto-router (commit 5c049f1) and the manual-waypoint
// editor — all rendering now uses ConnectorPath's smart bezier and
// stored waypoints (if any from legacy data) are ignored on render.
